import { api } from "services/customBaseQuery";
import API_URLS from "@constants/apiUrls";
import { transformErrorResponse } from "services/apiError";

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Login mutation
     */
    login: build.mutation<any, { email: string; password: any }>({
      query: (bodyData) => ({
        url: API_URLS.LOGIN,
        method: "POST",
        body: bodyData,
        credentials: "include",
      }),
    }),

    /**
     * Verify email mutation
     */
    verifyEmail: build.mutation<
      any,
      { username: string; is_encrypted: boolean }
    >({
      query: (bodyData) => ({
        method: "POST",
        url: API_URLS.VERIFY_USER,
        body: bodyData,
        credentials: "include",
      }),
      transformErrorResponse,
    }),

    /**
     * Get OAuth token mutation
     */
    getToken: build.mutation<
      { access_token: string },
      { client_id: string; client_secret: string; grant_type: string }
    >({
      query: (bodyData) => {
        const formData = new FormData();
        formData.append("client_id", bodyData.client_id);
        formData.append("client_secret", bodyData.client_secret);
        formData.append("grant_type", bodyData.grant_type);
        return {
          url: API_URLS.OAUTH_TOKEN,
          method: "POST",
          body: formData,
        };
      },
    }),

    /**
     * Verify OTP for login mutation
     */
    verifyOtpLogin: build.mutation<any, { otp: string; token: string }>({
      query: ({ otp, token }) => {
        const formData = new FormData();
        formData.append("otp", otp);
        return {
          url: `${API_URLS.VERIFY_OTP + token}/`,
          method: "POST",
          body: formData,
          credentials: "include",
        };
      },
    }),

    /**
     * Verify OTP for forgot password mutation
     */
    verifyOtpForgotPassword: build.mutation<
      any,
      { otp: string; token: string; username: string }
    >({
      query: ({ otp, token, username }) => {
        const formData = new FormData();
        formData.append("otp", otp);
        formData.append("username", username);
        return {
          url: `${API_URLS.VERIFY_FORGOT_PASSWORD_OTP + token}/`,
          method: "POST",
          body: formData,
          credentials: "include",
        };
      },
    }),

    /**
     * Resend OTP for login mutation
     */
    resendOtpLogin: build.mutation({
      query: ({ username, token, action }) => ({
        method: "POST",
        url: `${API_URLS.RESEND_OTP + token}/`,
        body: { username, action },
        credentials: "include",
      }),
    }),

    /**
     * Resend OTP for forgot password mutation
     */
    resendOtpForgotPassword: build.mutation({
      query: ({ username, token, action }) => {
        const formData = new FormData();
        formData.append("username", username);
        formData.append("action", action);

        return {
          method: "POST",
          url: `${API_URLS.RESEND_FORGOT_PASSWORD_OTP + token}/`,
          body: formData,
          credentials: "include",
        };
      },
    }),

    /**
     * Forgot password OTP request mutation
     */
    ForgotPasswordOtp: build.mutation({
      query: (emailPayload) => ({
        method: "POST",
        url: API_URLS.RESET_PASSWORD_REQUEST,
        body: emailPayload,
        credentials: "include",
      }),
      transformErrorResponse,
    }),

    /**
     * Reset password mutation
     */
    ResetPassword: build.mutation({
      query: ({ new_password1, new_password2, username }) => {
        const formData = new FormData();
        formData.append("new_password1", new_password1);
        formData.append("new_password2", new_password2);
        formData.append("username", username);

        return {
          method: "POST",
          url: API_URLS.RESET_PASSWORD_SUBMIT,
          body: formData,
          credentials: "include",
        };
      },
    }),

    /**
     * Refresh token mutation
     */
    refreshToken: build.mutation<
      { access: string; refresh: string },
      { refresh: string }
    >({
      query: ({ refresh }) => ({
        url: API_URLS.REFRESH_TOKEN,
        method: "POST",
        body: { refresh },
      }),
    }),

    /**
     * Logout mutation
     */
    logout: build.mutation<any, void>({
      query: () => ({
        url: API_URLS.LOG_OUT,
        method: "POST",
        credentials: "include",
      }),
    }),
  }),
  overrideExisting: false,
});

/**
 * Auto-generated hooks for auth API endpoints
 * @description RTK Query hooks for use in React components
 *
 * @exports useLoginMutation - Hook for login mutation
 * @exports useVerifyEmailMutation - Hook for email verification mutation
 * @exports useGetTokenMutation - Hook for OAuth token mutation
 * @exports useVerifyOtpLoginMutation - Hook for login OTP verification mutation
 * @exports useVerifyOtpForgotPasswordMutation - Hook for forgot password OTP verification mutation
 * @exports useResendOtpLoginMutation - Hook for resend login OTP mutation
 * @exports useRefreshTokenMutation - Hook for token refresh mutation
 * @exports useForgotPasswordOtpMutation - Hook for forgot password request mutation
 * @exports useResendOtpForgotPasswordMutation - Hook for resend forgot password OTP mutation
 * @exports useResetPasswordMutation - Hook for password reset mutation
 * @exports useLogoutMutation - Hook for logout mutation
 */
export const {
  useLoginMutation,
  useVerifyEmailMutation,
  useGetTokenMutation,
  useVerifyOtpLoginMutation,
  useVerifyOtpForgotPasswordMutation,
  useResendOtpLoginMutation,
  useRefreshTokenMutation,
  useForgotPasswordOtpMutation,
  useResendOtpForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
